import React, { useState, KeyboardEvent } from "react";
import styled from "styled-components";
import { Send } from "lucide-react";

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled,
  placeholder = "Type a message...",
}) => {
  const [text, setText] = useState("");

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <InputWrap>
      <TextArea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
      />
      <SendButton onClick={handleSend} disabled={disabled || !text.trim()} type="button">
        <Send size={24} />
      </SendButton>
    </InputWrap>
  );
};

export default ChatInput;

const InputWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.96);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(8px);
`;

const TextArea = styled.textarea`
  flex: 1;
  min-height: 46px;
  max-height: 120px;
  padding: 12px 16px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f8fafc;
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #171717;
  resize: none;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  &:focus {
    outline: none;
    border-color: #c94daa;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
  }
  &::placeholder {
    color: #9ca3af;
  }
  &:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(
    144.01deg,
    rgba(255, 201, 81, 0.95) 0%,
    rgba(206, 86, 176, 0.95) 100%
  );
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  transition: transform 0.18s ease, opacity 0.2s ease, box-shadow 0.18s ease;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(201, 77, 170, 0.3);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;
